/**
 * A line a newer run took over (bata-ui#166 W1-6, bata-platform ADR 6048, DEC-05/DEC-45).
 *
 * Since platform v0.51 the newest tick of a market supersedes the UNDECIDED lines of its earlier
 * runs: each such line reads `disposition: "superseded"`, with `superseded_by_run_id` and
 * `superseded_at`. It is never deleted, never decidable and never offered — the queues already
 * offer only lines whose `disposition` is null (ADR 6009), so a superseded line is shown as such and
 * not sent. A write that names one anyway (a queue read before the newer run landed) is refused
 * whole with `409 planning.inseason.line_superseded`, extensions `supersededIds` and
 * `supersededByRunId`: nothing was written, and the decision belongs on the newer run. This module
 * says that in the planner's words instead of a generic conflict.
 */

import { useTranslation } from 'react-i18next';

import { ApiProblemError } from '@/api';
import { DcPoolOvercommitNotice, dcPoolOvercommit, ProblemNotice, useRunNamer } from '@/planning';
import { StatusIndicator } from '@/primitives';

export const SUPERSEDED = 'superseded';
export const LINE_SUPERSEDED_CODE = 'planning.inseason.line_superseded';

export interface LineSupersededRefusal {
  readonly supersededIds: readonly string[];
  readonly supersededByRunId: string | null;
}

/** The refusal's extensions, or `null` for any other failure. */
export function lineSupersededRefusal(error: unknown): LineSupersededRefusal | null {
  if (!(error instanceof ApiProblemError) || error.problem.code !== LINE_SUPERSEDED_CODE) return null;
  const ids = error.problem['supersededIds'];
  const by = error.problem['supersededByRunId'];
  return {
    supersededIds: Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : [],
    supersededByRunId: typeof by === 'string' && by !== '' ? by : null,
  };
}

/**
 * A failed decision write: the superseded refusal explained, a decision that would leave a cell above what the DC holds with the
 * cells it names (bata-platform ADR 6220), anything else through the shared problem catalogue.
 */
export function DecisionWriteFailure({ error, titleKey }: { readonly error: unknown; readonly titleKey: string }): React.JSX.Element {
  const { t } = useTranslation();
  const namer = useRunNamer();
  const refusal = lineSupersededRefusal(error);
  const overcommitted = dcPoolOvercommit(error);
  if (overcommitted !== null) return <DcPoolOvercommitNotice error={error} cells={overcommitted} />;
  if (refusal === null) return <ProblemNotice error={error} titleKey={titleKey} />;
  return (
    <div role="alert" className="border-hairline rounded-control flex flex-col gap-2 border p-3" data-refusal={LINE_SUPERSEDED_CODE}>
      <StatusIndicator level="warning" labelKey="inseason.superseded.refusedTitle" values={{ count: refusal.supersededIds.length }} />
      <p className="text-body text-ink-primary max-w-prose">
        {refusal.supersededByRunId === null
          ? t('inseason.superseded.refusedBodyUnnamed')
          : t('inseason.superseded.refusedBody', { run: namer.slot(refusal.supersededByRunId) })}
      </p>
      <p className="text-caption text-ink-secondary max-w-prose">{t('inseason.superseded.nothingWritten')}</p>
    </div>
  );
}
